/* eslint-disable react/react-in-jsx-scope */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Link as MUILink,
  Breadcrumbs,
  Typography,
  Button,
  useTheme,
  Container,
  SelectChangeEvent,
  Drawer,
  CircularProgress,
  SvgIcon,
  Chip,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";
import ProviderCard from "./ProviderCard";
import { Service, FuneralHome } from "./listing-page.types";
import { FilteredProvidersParams } from "../../api/api.types";
import Filters from "./filters";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import SearchSort from "./search-sort";
import MobileFilters from "../../common/components/mobile-filters/MobileFilters";
import { ReactComponent as FilterBtnIcon } from "../../assets/icons/filter-btn-icon.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/closeIcon.svg";
import PaginationComponentNew from "../../common/components/pagination-component-new/pagination-component-new";
import {
  getFilteredProviders,
  getInitialProviders,
  getServices,
  requestProviderQuote,
} from "../../api/listing-page.api";
import {
  providerAutoSuggestions,
  getProviderBySearchText,
} from "../../api/details-page.api";
import { useSelector } from "react-redux";
import { getCityName } from "../../api/mapbox";
import EnableLocation from "../../common/components/enable-location-component/enable-location";
import CustomBreadcrumbs from "../../common/components/breadcrumb-component-new/breadcrumb-component-new";
import QuoteModal from "../../common/components/quote-modal-component/QuoteModal";
import PlannerSuccessModal from "../../common/components/planner-success-modal/PlannerSuccessModal";
import CompareWidget from "./components/CompareWidget";
import CompareScreen from "./components/CompareScreen";
import InfoPopup from "../../common/components/info-popup/info-popup";
import ToastMessage from "../../common/components/toast-message/toast-message";
import "./listing-page.scss";
import { getCategories } from "../../api/admin/service-management";

interface Suggestion {
  display: string;
  type: "planner" | "location" | "partner";
  data: unknown;
}

export interface FiltersState {
  services: Record<number, boolean>;
  needType: "at_need" | "pre_need" | "";
  priceRange?: [number, number];
  distanceRange?: [number, number];
  ratings?: number[];
  userLat?: number | null;
  userLong?: number | null;
  sortBy?: string;
  // searchText?: string;
}

export default function ListingPage() {
  const location = useLocation();
  const formData = location.state;
  const [searchServiceId, setSearchServiceId] = useState<number | null>(location?.state?.searchServiceId);

  const initialFilterParams = useMemo(() => {
    const sessionFilters = sessionStorage.getItem("providerFilters");
    if (formData) {
      sessionStorage.removeItem("providerFilters");
    }
    return sessionFilters;
  }, [formData]);

  const parsedFilterParams: FiltersState | null = initialFilterParams
    ? JSON.parse(initialFilterParams)
    : null;

  // Constants

  const DEFAULT_FILTERS: FiltersState = {
    services: parsedFilterParams?.services || {},
    needType: parsedFilterParams?.needType || "",
    priceRange: parsedFilterParams?.priceRange,
    distanceRange: parsedFilterParams?.distanceRange,
    ratings: parsedFilterParams?.ratings || [],
    userLat: parsedFilterParams?.userLat || 0,
    userLong: parsedFilterParams?.userLong || 0,
    sortBy: parsedFilterParams?.sortBy || "distance_low_to_high",
    // searchText: parsedFilterParams?.searchText || "",
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const pathnames = location.pathname.split("/").filter((x) => x);
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const itemsPerPage = 10;

  const [funeralHomes, setFuneralHomes] = useState<FuneralHome[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [masterCategories, setMasterCategories] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [listView, setListView] = useState<boolean>(true);

  useEffect(() => {
    const savedView = localStorage.getItem("listView");
    if (isMobile) {
      setListView(false);
    } else if (savedView !== null) {
      setListView(JSON.parse(savedView));
    }
  }, [isMobile]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [filters, setFilters] = useState<FiltersState>(DEFAULT_FILTERS);
  const [searchText, setSearchText] = useState<string>(
    sessionStorage.getItem("providerSearchText") || ""
  );
  const [debouncedSearchValue] = useDebounce(searchText ?? "", 300);
  const [userLat, setUserLat] = useState<number>(0);
  const [userLng, setUserLng] = useState<number>(0);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(itemsPerPage);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [locationPrompt, setLocation] = useState(false);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [preSearchProviders, setPreSearchProviders] = useState<
    FuneralHome[] | null
  >(null);
  const [totalCount, setTotalCount] = useState(0);
  const isInitialLocationEffectRun = useRef(true);
  const isInitialMount = useRef(true);
  const searchCategoryId = queryParams.get("serviceCategoryId");
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [preSearchTotalCount, setPreSearchTotalCount] = useState(0);
  const [sortSource, setSortSource] = useState<"filters" | "searchSort">(
    "filters"
  );
  const hasInitializedSearchText = useRef(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<FuneralHome | null>(null);
  const [showQuoteSuccess, setShowQuoteSuccess] = useState(false);
  const [compareList, setCompareList] = useState<FuneralHome[]>([]);
  const [showCompareScreen, setShowCompareScreen] = useState(false);
  const [showCompareLimitPopup, setShowCompareLimitPopup] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info" as "success" | "info" | "warning" | "error",
  });

  const isPreproductionEnv =
    (process.env.REACT_APP_ENV || process.env.NODE_ENV) === "preproduction";
  const isProductionEnv =
    (process.env.REACT_APP_ENV || process.env.NODE_ENV) === "production";


  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleCompareToggle = (providerId: number) => {
    const isAlreadyAdded = compareList.some((p) => p.id === providerId);
    if (isAlreadyAdded) {
      const updatedList = compareList.filter((p) => p.id !== providerId);
      setCompareList(updatedList);
      if (showCompareScreen && updatedList.length < 2) {
        setShowCompareScreen(false);
        setToast({
          open: true,
          message: "At least 2 items are needed for comparison",
          severity: "warning",
        });
      }
    } else {
      if (compareList.length >= 5) {
        setShowCompareLimitPopup(true);
        return;
      }
      const provider = funeralHomes.find((p) => p.id === providerId);
      if (provider) {
        setCompareList([...compareList, provider]);
      }
    }
  };

  const handleRequestQuote = (provider: FuneralHome) => {
    setSelectedProvider(provider);
    setIsQuoteModalOpen(true);
  };

  useEffect(() => {
    const storedSearches = localStorage.getItem("provider_recent_searches");
    if (storedSearches) {
      setRecentSearches(JSON.parse(storedSearches));
    }
  }, []);

  const addRecentSearch = (searchTerm: string) => {
    const updatedSearches = [
      searchTerm,
      ...recentSearches.filter((search) => search !== searchTerm),
    ].slice(0, 5);
    setRecentSearches(updatedSearches);
    localStorage.setItem(
      "provider_recent_searches",
      JSON.stringify(updatedSearches)
    );
  };

  const removeRecentSearch = (searchTerm: string) => {
    const updatedSearches = recentSearches.filter(
      (search) => search !== searchTerm
    );
    setRecentSearches(updatedSearches);
    localStorage.setItem(
      "provider_recent_searches",
      JSON.stringify(updatedSearches)
    );
  };

  const handleRecentSearchClick = (searchTerm: string) => {
    setSearchText(searchTerm);
    setShowSuggestions(true);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem("provider_recent_searches");
  };

  useEffect(() => {
    if (locationPrompt) {
      setOpenLocationDialog(true);
      setInfoMessage(
        "Please enable or enter location to view funeral homes near you."
      );
    }
  }, [locationPrompt]);

  const selectedServiceId = formData?.selectedServiceId;


  const areServicesSelected = useMemo(
    () =>
      Object.values(filters.services || {}).some(Boolean) &&
      funeralHomes?.length > 0,
    [filters.services, funeralHomes]
  );

  const isPreNeedServiceSelected = useMemo(() => {
    if (!filters.services) {
      return false;
    }
    const selectedServiceIds = Object.keys(filters.services || {}).filter(
      (id) => filters.services?.[Number(id)]
    );

    if (selectedServiceIds?.length === 0) {
      return false;
    }

    return selectedServiceIds?.some((id) => {
      const service = allCategories?.find((s) => s.id === Number(id));
      return service?.needTypes?.includes("pre_need");
    });
  }, [filters.services, allCategories]);

  const isAtNeedServiceSelected = useMemo(() => {
    if (!filters.services) {
      return false;
    }

    const selectedServiceIds = Object.keys(filters.services || {}).filter(
      (id) => filters.services?.[Number(id)]
    );

    if (selectedServiceIds.length === 0) {
      return false;
    }

    return selectedServiceIds.some((id) => {
      const service = allCategories.find((s) => s.id === Number(id));
      if (!service) return false;

      const needTypes = service.needTypes || [];

      const supportsAtNeed = needTypes.includes("at_need");
      const supportsPreNeed = needTypes.includes("pre_need");
      if (supportsAtNeed && supportsPreNeed) return false;

      return supportsAtNeed;
    });
  }, [filters.services, allCategories]);

  const sortOptions = [
    { value: "price_high_to_low", label: "Price(High-Low)" },
    { value: "price_low_to_high", label: "Price(Low-High)" },
    { value: "distance_low_to_high", label: "Distance" },
    { value: "rating_high_to_low", label: "Rating" },
  ];

  const storedLocation = localStorage.getItem("user_location");
  let latitude = 0;
  let longitude = 0;

  if (storedLocation) {
    const loc = JSON.parse(storedLocation);
    latitude = loc.latitude;
    longitude = loc.longitude;
  }

  const { latitude: reduxLat, longitude: reduxLng } = useSelector(
    (state: { userLocation: { latitude: number; longitude: number } }) =>
      state.userLocation
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue === "") {
      handleFiltersChange(filters, 1);
    }
    setSearchText(newValue);
    setShowSuggestions(true);
  };

  const handleHomeNavigation = () => {
    sessionStorage.removeItem("providerFilters");
    sessionStorage.removeItem("searchServiceId");
    setSearchServiceId(null);
    navigate("/home");
  };

  const fetchSuggestions = async () => {
    if (!debouncedSearchValue.trim() || !showSuggestions) {
      setSuggestions([]);
      return;
    }
    try {
      setSuggestionLoading(true);
      const response = await providerAutoSuggestions(debouncedSearchValue);
      const { partners, locations } = response.data.data;
      const partnerSuggestions = partners.map((p: { name: string }) => ({
        display: `${p.name}`,
        type: "partner",
        data: p,
      }));
      const locationSuggestions = locations.map(
        (l: { city: string; state: string }) => ({
          display: `${l.city}, ${l.state}`,
          type: "location",
          data: l,
        })
      );

      const combined = [...partnerSuggestions, ...locationSuggestions];
      const uniqueSuggestions = Array.from(
        new Map(combined.map((item) => [item.display, item])).values()
      );
      setSuggestions(uniqueSuggestions);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
    } finally {
      setSuggestionLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [debouncedSearchValue, showSuggestions]);

  useEffect(() => {
    if (!hasInitializedSearchText.current) {
      hasInitializedSearchText.current = true;
      sessionStorage.setItem("providerSearchText", searchText);
      return;
    }

    sessionStorage.setItem("providerSearchText", searchText);
    if (searchText === "") {
      handleFiltersChange(filters, 1);
    }
  }, [searchText]);

  const getProvidersBySearch = async (params: {
    businessName?: string;
    type?: string;
    state?: string;
    city?: string;
    text?: string;
  }) => {
    if (!preSearchProviders) {
      setPreSearchProviders(funeralHomes);
    }
    setLoading(true);
    try {
      setCurrentPage(1);
      const response = await getProviderBySearchText(params);
      setFuneralHomes(response.data.data?.data);
      if (response.data.data.data?.length === 0) {
        setInfoMessage("No providers found");
      } else {
        setInfoMessage("");
      }
      if (response?.data?.success) {
        setFuneralHomes(response.data.data.data);
        setTotalCount(response.data.data.totalCount || 0);
      }
      return response;
    } catch (error) {
      console.error("Failed to fetch providers by search:", error);
      setInfoMessage("Failed to fetch providers. Please try again.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchTerm: string) => {
    addRecentSearch(searchTerm);
    if (searchTerm.trim() !== "") {
      setSearchText(searchTerm);
      getProvidersBySearch({ text: searchTerm });
    }
  };

  const handleSuggestionClick = async (suggestion: Suggestion) => {
    addRecentSearch(suggestion.display);
    if (!preSearchProviders) {
      setPreSearchProviders(funeralHomes);
      setPreSearchTotalCount(totalCount);
    }

    setSearchText(suggestion.display);
    setSuggestions([]);
    setShowSuggestions(false);

    try {
      setLoading(true);
      setCurrentPage(1);
      let response;

      if (suggestion.type === "partner") {
        response = await getProvidersBySearch({
          businessName: (suggestion.data as { name: string }).name,
          type: "partner",
        });
      } else if (suggestion.type === "location") {
        response = await getProvidersBySearch({
          city: (suggestion.data as { city: string }).city,
          state: (suggestion.data as { state: string }).state,
          type: "location",
        });
      }

      if (response?.data?.success) {
        setFuneralHomes(response.data.data.data);
        setTotalCount(response.data.data.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching providers by search:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    const newSortValue = event.target.value;

    setSortSource("searchSort");

    handleFiltersChange({ ...filters, sortBy: newSortValue }, 1);
  };

  const handlePlanners = () => {
    navigate("preneed-planners", { state: { clearFilters: true } });
  };

  const filteredProviders = useMemo(() => {
    if (!Array.isArray(funeralHomes)) return [];

    let result = [...funeralHomes];

    switch (filters.sortBy) {
      case "price_low_to_high":
        result = [...result].sort((a, b) => (a.startingPrice ?? 0) - (b.startingPrice ?? 0));
        break;
      case "price_high_to_low":
        result = [...result].sort((a, b) => (b.startingPrice ?? 0) - (a.startingPrice ?? 0));
        break;
      case "distance_low_to_high":
        result = [...result].sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
        break;
      case "rating_high_to_low":
        result = [...result].sort((a, b) => (b.avgRating ?? 0) - (a.avgRating ?? 0));
        break;
    }

    return result;
  }, [filters.sortBy, funeralHomes]);

  const paginatedProviders = useMemo(() => {
    if (isMobile) {
      return filteredProviders.slice(0, mobileVisibleCount);
    }

    return filteredProviders;
  }, [filteredProviders, isMobile, mobileVisibleCount]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const renderComingSoon = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 8, md: 12 },
        px: 4,
        textAlign: "center",
        background:
          "linear-gradient(135deg, rgba(16, 65, 69, 0.04) 0%, rgba(159, 127, 44, 0.04) 100%)",
        borderRadius: "16px",
        border: "1px solid rgba(16, 65, 69, 0.08)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 8px 32px 0 rgba(16, 65, 69, 0.03)",
        maxWidth: "600px",
        margin: "2rem auto",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #104145 0%, #1c5b60 100%)",
          color: "#fff",
          mb: 3,
          boxShadow: "0 6px 20px rgba(16, 65, 69, 0.25)",
          animation: "pulse-flower 2.5s infinite ease-in-out",
          "@keyframes pulse-flower": {
            "0%": {
              transform: "scale(0.96)",
              boxShadow: "0 6px 15px rgba(16, 65, 69, 0.2)",
            },
            "50%": {
              transform: "scale(1.04)",
              boxShadow: "0 6px 25px rgba(16, 65, 69, 0.35)",
            },
            "100%": {
              transform: "scale(0.96)",
              boxShadow: "0 6px 15px rgba(16, 65, 69, 0.2)",
            },
          },
        }}
      >
        <LocalFloristIcon sx={{ fontSize: 34 }} />
      </Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: "#104145",
          mb: 1.5,
          letterSpacing: "-0.5px",
        }}
      >
        Coming Soon
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "rgba(16, 65, 69, 0.75)",
          maxWidth: "400px",
          lineHeight: 1.6,
          fontSize: "16px",
        }}
      >
        Funeral homes will be listed here soon.
      </Typography>
    </Box>
  );

  const renderMobileListings = () => {
    if (isPreproductionEnv || isProductionEnv) {
      return renderComingSoon();
    }
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
          }}
        >
          <CircularProgress
            size="2.5rem"
            sx={{
              color: "#104145",
            }}
          />
        </Box>
      );
    }

    if (paginatedProviders?.length > 0) {
      return (
        <>
          {paginatedProviders.map((home, index) => {
            const preloadIndex = mobileVisibleCount - 2;
            const showSentinel = index === preloadIndex;

            return (
              <React.Fragment key={home.id}>
                <ProviderCard
                  title={home.businessName}
                  description={home.description}
                  services={home.services}
                  price={home.startingPrice}
                  images={home.fileInfo}
                  layout={listView ? "list" : "grid"}
                  businessType={home.businessType}
                  location={home.location}
                  avgRating={home.avgRating}
                  contactInfo={home.contactInfo}
                  address={home.address}
                  phoneNumber={home.phoneNumber}
                  email={home.email}
                  isFavorite={home.isFavorite}
                  id={home.id}
                  highlightText={searchText}
                  onRequestQuote={handleRequestQuote}
                  isCompareSelected={compareList.some((p) => p.id === home.id)}
                  onCompareToggle={handleCompareToggle}
                />
                {showSentinel && (
                  <Box
                    ref={loadMoreRef}
                    sx={{
                      height: 1,
                      opacity: 0,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}

          {isLoadingMore && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2,
              }}
            >
              <CircularProgress
                size="2.5rem"
                className="spinner-details-page__spinner"
              />
            </Box>
          )}
        </>
      );
    }

    return (
      <Typography
        variant="body1"
        sx={{ mt: 2, color: "text.secondary", textAlign: "center" }}
      >
        {infoMessage || "No providers found"}
      </Typography>
    );
  };

  const renderDesktopListings = () => {
    if (isPreproductionEnv || isProductionEnv) {
      return renderComingSoon();
    }
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 2,
            color: "#104145",
            width: "100%",
          }}
        >
          <CircularProgress
            size="3rem"
            className="spinner-details-page__spinner"
          />
        </Box>
      );
    }

    if (paginatedProviders?.length > 0) {
      return paginatedProviders.map((home) => (
        <ProviderCard
          key={home.id}
          title={home.businessName}
          description={home.description}
          services={home.services}
          price={home.startingPrice}
          images={home.fileInfo}
          layout={listView ? "list" : "grid"}
          businessType={home.businessType}
          location={home.location}
          avgRating={home.avgRating}
          contactInfo={home.contactInfo}
          address={home.address}
          phoneNumber={home.phoneNumber}
          email={home.email}
          isFavorite={home.isFavorite}
          id={home.id}
          highlightText={searchText}
          onRequestQuote={handleRequestQuote}
          isCompareSelected={compareList.some((p) => p.id === home.id)}
          onCompareToggle={handleCompareToggle}
        />
      ));
    }

    return (
      <Typography
        variant="body1"
        sx={{
          mt: 10,
          color: "text.secondary",
          textAlign: "center",
          width: "99%",
        }}
      >
        {infoMessage || "No providers found"}
      </Typography>
    );
  };

  // const fetchServices = async () => {
  //   setLoading(true);

  //   try {
  //     const response = await getServices("all");
  //     const typeOfServices: Service[] = response.data.data.data || [];
  //     setAllServices(typeOfServices);

  //     const initialFilterParams = sessionStorage.getItem("providerFilters");
  //     if (initialFilterParams && !formData) {
  //       setLoading(false);
  //       return;
  //     }

  //     const initialServicesState: Record<number, boolean> = {};
  //     typeOfServices.forEach((service) => {
  //       const isSelectedByForm = formData?.serviceType === service.id;

  //       const isSelectedById =
  //         selectedServiceId && service.id === selectedServiceId;

  //       initialServicesState[service.id] = isSelectedByForm || isSelectedById;
  //     });

  //     // checking service for search from home
  //     if (searchServiceId && Number(searchServiceId) <= typeOfServices.length) {
  //       initialServicesState[Number(searchServiceId)] = true;
  //     }

  //     const needTypeValue = formData?.needType ?? formData?.selectedNeedType;

  //     const newFilters: FiltersState = {
  //       services: initialServicesState,
  //       needType:
  //         formData?.needType === "immediate"
  //           ? "at_need"
  //           : formData?.needType === "pre_need"
  //             ? "pre_need"
  //             : "",
  //       priceRange: parsedFilterParams?.priceRange,
  //       distanceRange: parsedFilterParams?.distanceRange,
  //       userLat: userLat,
  //       userLong: userLng,
  //       sortBy: filters.sortBy,
  //     };

  //     sessionStorage.setItem("providerFilters", JSON.stringify(newFilters));

  //     setFilters(newFilters);
  //   } catch (error) {
  //     console.error("Failed to fetch services:", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleLocation = async () => {
    if (formData?.latitude && formData?.longitude) {
      setUserLat(formData.latitude);
      setUserLng(formData.longitude);

      // get city info
      const response = await getCityName(formData.latitude, formData.longitude);

      const cityName =
        response?.data?.features?.[0]?.text_en ||
        response?.data?.features?.[0]?.text;

      localStorage.setItem(
        "user_location",
        JSON.stringify({
          latitude: formData.latitude,
          longitude: formData.longitude,
          city: cityName,
        })
      );
    } else {
      // else get from localStorage for carousel
      const storedLocation = localStorage.getItem("user_location");
      if (storedLocation) {
        const { latitude, longitude } = JSON.parse(storedLocation);
        setUserLat(latitude);
        setUserLng(longitude);
      }
    }
  };

  useEffect(() => {
    handleLocation();
  }, [formData]);

  useEffect(() => {
    localStorage.setItem("listView", JSON.stringify(listView));
  }, [listView]);

  const isFirstLoad = useRef(true);

  useEffect(() => {
    isFirstLoad.current = true;
  }, [formData]);

  useEffect(() => {
    if (formData && formData.providers) {
      setFuneralHomes(formData.providers);
      if (formData.totalCount) {
        setTotalCount(formData.totalCount);
      }
    }
  }, [formData]);

  useEffect(() => {
    if (!latitude || !longitude) {
      setLocation(true);
    } else {
      setLocation(false);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (!formData?.selectedNeedType) return;

    const initialFilterParams = sessionStorage.getItem("providerFilters");
    const parsedFilterParams: FiltersState | null = initialFilterParams
      ? JSON.parse(initialFilterParams)
      : null;

    if (
      parsedFilterParams &&
      Object.keys(parsedFilterParams.services || {}).length > 0 &&
      !formData
    ) {
      const timer = setTimeout(() => {
        handleFiltersChange(parsedFilterParams);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [allCategories]);

  const handleFiltersChange = async (
    newFilters: FiltersState,
    pageNumber: number = 1
  ) => {
    const savedSearchText = sessionStorage.getItem("providerSearchText");
    if (savedSearchText) return;
    setLoading(true);
    setCurrentPage(pageNumber);

    if (isFirstLoad.current && formData) {
      isFirstLoad.current = false;
      setLoading(false);
      return;
    }

    if (isFirstLoad.current && !formData) {
      isFirstLoad.current = false;
    }

    sessionStorage.setItem("providerFilters", JSON.stringify(newFilters));

    if (newFilters.needType !== filters.needType) {
      newFilters.services = {};
      setSearchServiceId(null);
      sessionStorage.removeItem("searchServiceId");
      sessionStorage.setItem("providerFilters", JSON.stringify(newFilters));
      const params = new URLSearchParams(location.search);
      if (params.has("serviceCategoryId")) {
        params.delete("serviceCategoryId");
        navigate({
          pathname: location.pathname,
          search: params.toString(),
        }, { replace: true });
      }
    }

    setFilters(newFilters);

    const selectedServices = Object.keys(newFilters.services || {}).filter(
      (s) => newFilters.services?.[Number(s)]
    );

    // No service selected but needType is chosen
    if (selectedServices.length === 0 && newFilters.needType && !searchServiceId) {
      setFuneralHomes([]);
      setTotalCount(0);
      setCurrentPage(1);
      setMobileVisibleCount(itemsPerPage);
      setInfoMessage("Please select service category to show providers");
      setLoading(false);
      return;
    }

    if (selectedServices.length === 0 && !newFilters.needType && !searchServiceId) {
      setFuneralHomes([]);
      setTotalCount(0);
      setCurrentPage(1);
      setMobileVisibleCount(itemsPerPage);
      setInfoMessage("Please select need type and service category to show providers");
      setLoading(false);
      return;
    }

    if (!(reduxLat || latitude) || !(reduxLng || longitude)) {
      setFuneralHomes([]);
      setTotalCount(0);
      setCurrentPage(1);
      setMobileVisibleCount(itemsPerPage);
      setInfoMessage(
        "Please enable or enter location to view funeral homes near you."
      );
      setLoading(false);
      return;
    }

    try {
      const params: FilteredProvidersParams = {
        userLat: latitude || reduxLat,
        userLng: longitude || reduxLng,
        ...(newFilters.needType && {
          type: newFilters.needType as "" | "at_need" | "pre_need",
        }),
        // categoryId: selectedCategories,
        serviceCategoryId: selectedServices,
        ...(newFilters.priceRange && {
          minPrice: newFilters.priceRange[0],
          maxPrice: newFilters.priceRange[1],
        }),
        ...(newFilters.distanceRange && {
          minDistance: Math.round(newFilters.distanceRange[0] * 1.60934),
          maxDistance: Math.round(newFilters.distanceRange[1] * 1.60934),
        }),
        ...(newFilters.ratings?.length && { rating: newFilters.ratings }),
        ...(newFilters.sortBy && { sortBy: newFilters.sortBy }),
        page: pageNumber,
        pageSize: itemsPerPage,
      };
      //if any other filters selected than serviceId from location.state should be ignored
      //and search with selected filters only 
      const areAnyFiltersSelected =
        (newFilters.services &&
          Object.values(newFilters.services).some((v) => v)) ||
        newFilters.priceRange ||
        newFilters.distanceRange ||
        (newFilters.ratings && newFilters.ratings.length > 0) ||
        newFilters.needType;

      if (!areAnyFiltersSelected && searchServiceId) {
        params.serviceId = searchServiceId;
      }
      //if filters changed then remove searchServiceId from sessionStorage
      if (areAnyFiltersSelected) {
        sessionStorage.removeItem("searchServiceId");
        setSearchServiceId(null);
      }
      const response = await getFilteredProviders(params);
      const funeralHomesData = response.data.data.data;
      const totalCountData = response.data.data.totalCount;

      setFuneralHomes(funeralHomesData);
      setTotalCount(totalCountData);

      if (funeralHomesData.length === 0) {
        setInfoMessage("No providers found");
      } else {
        setInfoMessage("");
      }
    } catch (error) {
      console.error("Filter fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    handleFiltersChange(filters, currentPage);
  }, [currentPage]);

  // useEffect(() => {
  //   if (!reduxLat || !reduxLng) return;
  //   if (!filters || Object.keys(filters.services || {}).length === 0) return;

  //   handleFiltersChange(filters, 1);
  // }, [reduxLat, reduxLng]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          !isLoadingMore &&
          mobileVisibleCount < filteredProviders.length
        ) {
          setIsLoadingMore(true);
          setMobileVisibleCount((prev) => prev + itemsPerPage);
          setIsLoadingMore(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    // stop observing when rerender/unmounts
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [isLoadingMore, mobileVisibleCount, filteredProviders.length]);

  const refetchProviders = async () => {
    const savedSearchText = sessionStorage.getItem("providerSearchText");
    if (savedSearchText) return;
    setCurrentPage(1);
    try {
      setLoading(true);

      const initialFilterParams = sessionStorage.getItem("providerFilters");

      const parsedFilterParams: FiltersState | null = initialFilterParams
        ? JSON.parse(initialFilterParams)
        : null;

      const effectiveFilters: FiltersState = parsedFilterParams || filters;

      const selectedServices = Object.keys(
        effectiveFilters.services || {}
      ).filter((s) => effectiveFilters.services?.[Number(s)]);

      // No service selected but needType is chosen
      if (selectedServices.length === 0 && effectiveFilters.needType && !searchServiceId) {
        setFuneralHomes([]);
        setTotalCount(0);
        setCurrentPage(1);
        setMobileVisibleCount(itemsPerPage);
        setInfoMessage("Please select service category to show providers");
        setLoading(false);
        return;
      }

      // No service selected and no needType chosen
      if ((selectedServices.length === 0 && !effectiveFilters.needType && !searchServiceId)) {
        setFuneralHomes([]);
        setTotalCount(0);
        setCurrentPage(1);
        setMobileVisibleCount(itemsPerPage);
        setInfoMessage("Please select need type and service category to show providers");
        setLoading(false);
        return;
      }

      const params: FilteredProvidersParams = {
        userLat: reduxLat,
        userLng: reduxLng,
        ...(effectiveFilters.needType && {
          type: effectiveFilters.needType as "" | "at_need" | "pre_need",
        }),
        serviceCategoryId: selectedServices,
        ...(effectiveFilters.priceRange && {
          minPrice: effectiveFilters.priceRange[0],
          maxPrice: effectiveFilters.priceRange[1],
        }),
        ...(effectiveFilters.distanceRange && {
          minDistance: Math.round(effectiveFilters.distanceRange[0] * 1.60934),
          maxDistance: Math.round(effectiveFilters.distanceRange[1] * 1.60934),
        }),
        ...(effectiveFilters.ratings?.length && {
          rating: effectiveFilters.ratings,
        }),
        ...(effectiveFilters.sortBy && { sortBy: effectiveFilters.sortBy }),
        page: 1, // Reset to page 1 on location change
        pageSize: itemsPerPage,
      };

      const response = await getInitialProviders(params);
      const funeralHomesData = response.data.data?.data;
      const totalCountData = response.data.data.totalCount;

      setFuneralHomes(funeralHomesData);
      setTotalCount(totalCountData);

      if (funeralHomesData.length === 0) {
        setInfoMessage("No providers found");
      } else {
        setInfoMessage("");
      }
    } catch (error) {
      console.error("Re-fetch after location change failed:", error);
      // setInfoMessage("Unable to load providers for this location.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!reduxLat || !reduxLng || masterCategories.length === 0) return;

    if (isInitialLocationEffectRun.current) {
      isInitialLocationEffectRun.current = false;
      if (formData && formData.providers) {
        return;
      }
    }

    refetchProviders();
  }, [reduxLat, reduxLng, masterCategories, formData]);

  useEffect(() => {
    const savedSearchText = sessionStorage.getItem("providerSearchText");
    const savedSuggestion = sessionStorage.getItem("searchSuggestion");
    const parsedSuggestion = savedSuggestion
      ? JSON.parse(savedSuggestion)
      : null;
    if (savedSearchText && !parsedSuggestion) {
      handleSearch(savedSearchText);
      // sessionStorage.removeItem("providerSearchText");
    } else if (parsedSuggestion && !formData) {
      handleSuggestionClick(parsedSuggestion);
      // sessionStorage.removeItem("searchSuggestion");
    }
  }, []);

  // const filteredServices = useMemo(() => {
  //   if (!filters.needType) {
  //     return allServices;
  //   }
  //   return allServices.filter((service) =>
  //     filters.needType === "at_need" || filters.needType === "pre_need"
  //       ? service.needTypes.includes(filters.needType)
  //       : false
  //   );
  // }, [allServices, filters.needType]);

  const fetchAllCategories = async () => {
    try {
      const response = await getCategories({ type: 'all' });
      const fetchedCategories = response?.data?.data?.data || [];
      setMasterCategories(fetchedCategories);
      setAllCategories(fetchedCategories);
      //check for initial filters in session storage
      const initialFilterParams = sessionStorage.getItem("providerFilters");
      const parsedFilterParams: FiltersState | null = initialFilterParams
        ? JSON.parse(initialFilterParams)
        : null;

      if (parsedFilterParams && !formData && !searchCategoryId) {
        return;
      }
      let initialServicesState: Record<number, boolean> = parsedFilterParams?.services || {};
      let initialNeedType: "at_need" | "pre_need" | "" = parsedFilterParams?.needType || "";

      if (!parsedFilterParams) {
        //Category id searched on home page one from findService one from category cards
        if (searchCategoryId && Number(searchCategoryId)) {
          initialServicesState = { [Number(searchCategoryId)]: true };
        }
        // checking service for search from home
        else if (
          (formData?.serviceType && Number(formData.serviceType)) ||
          (formData?.selectedCategoryId && Number(formData.selectedCategoryId))
        ) {
          const categoryId =
            formData?.serviceType || formData?.selectedCategoryId;

          initialServicesState = { [Number(categoryId)]: true };
        }
        // services from Start Planning Today
        else if (formData?.services && Object.keys(formData?.services).length) {
          initialServicesState = formData.services;
        }

        initialNeedType =
          formData?.needType === "immediate"
            ? "at_need"
            : formData?.needType === "preneed"
              ? "pre_need"
              : "";
      }

      const newFilters: FiltersState = {
        services: initialServicesState,
        needType: initialNeedType,
        priceRange: parsedFilterParams?.priceRange,
        distanceRange: parsedFilterParams?.distanceRange,
        ratings: parsedFilterParams?.ratings,
        userLat: userLat,
        userLong: userLng,
        sortBy: filters.sortBy || parsedFilterParams?.sortBy || "distance_low_to_high",
      };

      sessionStorage.setItem("providerFilters", JSON.stringify(newFilters));

      setFilters(newFilters);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  useEffect(() => {
    fetchAllCategories();
  }, [formData, searchCategoryId]);

  //call provider list api on click of category card
  useEffect(() => {
    if (Number(formData?.selectedCategoryId)) {
      sessionStorage.removeItem("searchServiceId");
      handleFiltersChange(filters, 1);
    }
    if (Number(searchServiceId)) {
      handleFiltersChange(filters, 1);
    }
  }, [formData?.selectedCategoryId, searchServiceId]);

  //update categories list based on needType selected
  useEffect(() => {
    if (!masterCategories.length) return;

    if (filters.needType === "at_need") {
      const atNeedCategories = masterCategories.filter((category) =>
        category.needTypes.includes("at_need")
      );
      setAllCategories(atNeedCategories);
    }
    else if (filters.needType === "pre_need") {
      const preNeedCategories = masterCategories.filter((category) =>
        category.needTypes.includes("pre_need")
      );
      setAllCategories(preNeedCategories);
    }
    else {
      setAllCategories(masterCategories);
    }
  }, [filters.needType, masterCategories])

  //set searchServiceId from session storage to location state to persist on navigation
  useEffect(() => {
    const searchServiceId = sessionStorage.getItem("searchServiceId");
    if (searchServiceId) {
      setSearchServiceId(Number(searchServiceId));
    }
  }, []);
  //set searchServiceId to session storage from location state
  useEffect(() => {
    if (location?.state?.searchServiceId) {
      sessionStorage.setItem(
        "searchServiceId",
        String(location.state.searchServiceId)
      );
      setSearchServiceId(location.state.searchServiceId);
    }
  }, [location?.state?.searchServiceId])

  if (showCompareScreen) {
    return (
      <CompareScreen
        compareList={compareList}
        onRemove={handleCompareToggle}
        onBack={() => setShowCompareScreen(false)}
      />
    );
  }

  return (
    <Container
      maxWidth={false}
      className={`listing-page ${isMobile ? "mobile" : ""}`}
      sx={{
        padding: isMobile ? "0 !important" : "",
      }}
    >
      {!isMobile ? (
        <Box className="content-wrapper">
          {/* Sidebar Filters */}
          <Filters
            allServices={allServices}
            allCategories={allCategories}
            initialFilters={filters}
            onChange={handleFiltersChange}
            sortSource={sortSource}
            setSortSource={setSortSource}
          />

          {/* Main Content */}
          <Box className="main-content">
            {/* Top Controls */}
            <Box className="top-bar">
              <Box className="breadcrumb">
                <CustomBreadcrumbs
                  items={[
                    {
                      label: "Home",
                      to: "/home",
                      onClick: handleHomeNavigation,
                    },
                    {
                      label: "List of Funeral Homes",
                    },
                  ]}
                />
              </Box>
              <Box className="planners-view-toggle-section">
                {(filters.needType === "pre_need" ||
                  isPreNeedServiceSelected) &&
                  filters.needType !== "at_need" &&
                  !isAtNeedServiceSelected && (
                    <Button
                      className="planners-btn"
                      variant="outlined"
                      onClick={handlePlanners}
                    // disabled={!areServicesSelected} not required as this button is only selected once preneed need type is selected
                    >
                      Plan with Planners
                    </Button>
                  )}

                <Box className="view-toggle">
                  <Button
                    onClick={() => setListView(true)}
                    variant={listView ? "contained" : "outlined"}
                    className={`toggle-btn ${listView ? "active" : ""}`}
                  >
                    <ViewListRoundedIcon fontSize="small" />
                  </Button>
                  <Button
                    onClick={() => setListView(false)}
                    variant={!listView ? "contained" : "outlined"}
                    className={`toggle-btn ${!listView ? "active" : ""}`}
                  >
                    <GridViewOutlinedIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
            </Box>
            {/* Search and sort */}
            <SearchSort
              searchValue={searchText ?? ""}
              sortValue={filters.sortBy ?? ""}
              onSearchChange={handleSearchChange}
              onSortChange={handleSortChange}
              sortOptions={sortOptions}
              suggestions={suggestions}
              onSuggestionClick={handleSuggestionClick}
              onSearch={() => handleSearch(searchText)}
              loading={suggestionLoading}
              placeholder="Search Provider name or Location"
              recentSearches={recentSearches}
              onClearRecentSearches={clearRecentSearches}
              onRecentSearchClick={handleRecentSearchClick}
              onRemoveRecentSearch={removeRecentSearch}
            />

            {/* Listings */}
            <Box
              className={`funeral-homes ${listView ? "list-view" : "grid-view"}`}
            >
              {renderDesktopListings()}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ py: 2, px: 0.5 }} className="mobile-view-container">
          {/* Top bar with breadcrumbs + view toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
            className="mobile-breadcrumbs-toggle"
          >
            {/* Breadcrumbs */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "#104145",
                gap: 1,
              }}
              className="mobile-breadcrumbs"
            >
              <ArrowBackIosNewIcon />

              <Typography
                sx={{
                  fontSize: isMobile ? "1rem" : "1.2rem",
                  color: "#000",
                }}
              >
                List of Funeral Homes
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {(filters.needType === "pre_need" ||
                isPreNeedServiceSelected) &&
                filters.needType !== "at_need" &&
                !isAtNeedServiceSelected && (
                  <Button
                    className="planners-btn"
                    variant="outlined"
                    onClick={handlePlanners}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#15585D",
                      color: "#fff",
                      borderRadius: "6px",
                      fontSize: "12px",
                      padding: "4px 8px",
                      "&:hover": {
                        backgroundColor: "#15585D",
                        color: "#fff",
                      },
                    }}
                  >
                    Plan with Planners
                  </Button>
                )}
              <Box className="view-toggle">
                <Button
                  onClick={() => setListView(true)}
                  variant={listView ? "contained" : "outlined"}
                  className={`toggle-btn ${listView ? "active" : ""}`}
                >
                  <ViewListRoundedIcon fontSize="small" />
                </Button>
                <Button
                  onClick={() => setListView(false)}
                  variant={!listView ? "contained" : "outlined"}
                  className={`toggle-btn ${!listView ? "active" : ""}`}
                >
                  <GridViewOutlinedIcon fontSize="small" />
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Filters + Search + Sort in single row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              backgroundColor: "#F2F2F2",
              padding: 1,
            }}
          >
            {/* Filters Button - opens drawer */}
            <Button
              variant="outlined"
              onClick={() => setFilterDrawerOpen(true)}
              sx={{
                flexShrink: 0,
                bgcolor: "#d1d7d8",
                color: "#104145",
                textTransform: "capitalize",
                borderRadius: "8px",
                border: "none",
              }}
            >
              <SvgIcon
                component={FilterBtnIcon}
                inheritViewBox
                sx={{
                  width: "14px",
                  marginRight: "0.3rem",
                }}
              />
              Filters
            </Button>

            {/* Search & Sort inline */}
            <SearchSort
              searchValue={searchText ?? ""}
              sortValue={filters.sortBy ?? ""}
              onSearchChange={handleSearchChange}
              onSortChange={handleSortChange}
              sortOptions={sortOptions}
              suggestions={suggestions}
              onSuggestionClick={handleSuggestionClick}
              onSearch={() => handleSearch(searchText)}
              loading={suggestionLoading}
              placeholder="Search Provider name or Location"
              recentSearches={recentSearches}
              onClearRecentSearches={clearRecentSearches}
              onRecentSearchClick={handleRecentSearchClick}
              onRemoveRecentSearch={removeRecentSearch}
            />
          </Box>

          {filters.sortBy && (
            <Chip
              label={
                sortOptions.find((opt) => opt.value === filters.sortBy)
                  ?.label || filters.sortBy
              }
              onDelete={() => handleFiltersChange({ ...filters, sortBy: "" })}
              sx={{
                bgcolor: "#10414526",
                color: "#104145",
                fontWeight: 500,
                marginBottom: "1rem",
                fontSize: "14px",
                borderRadius: "8px",
                marginLeft: "0.5rem",
              }}
              deleteIcon={
                <CloseIcon
                  style={{
                    color: "#104145",
                  }}
                />
              }
            />
          )}

          {/* Provider listings */}
          <Box
            className={`funeral-homes ${listView ? "list-view" : "grid-view"}`}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {renderMobileListings()}
          </Box>

          {/* Filters Drawer */}
          <Drawer
            anchor="top"
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
          // PaperProps={{
          //   sx: {
          //     top: 100,
          //     height: "calc(100% - 100px)",
          //   },
          // }}
          >
            <Box sx={{ p: 1, overflowY: "auto" }}>
              <MobileFilters
                allServices={allServices}
                allCategories={allCategories}
                initialFilters={filters}
                onChange={handleFiltersChange}
                onCloseDrawer={() => setFilterDrawerOpen(false)}
                setListView={setListView}
                listView={listView}
              />
            </Box>
          </Drawer>
        </Box>
      )}

      {/* Desktop Pagination */}
      {totalPages > 0 && !isMobile && !isPreproductionEnv && !isProductionEnv && (
        <Box className="pagination-wrapper">
          <PaginationComponentNew
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </Box>
      )}

      <EnableLocation
        openDialog={openLocationDialog}
        onClose={() => setOpenLocationDialog(false)}
        showCloseIcon={false}
      />

      {selectedProvider && (
        <QuoteModal
          open={isQuoteModalOpen}
          onClose={() => setIsQuoteModalOpen(false)}
          title={selectedProvider.businessName}
          description="Use this form to reach out and share your preneed planning preferences"
          partnerId={selectedProvider.id}
          onSuccess={() => {
            setIsQuoteModalOpen(false);
            setShowQuoteSuccess(true);
          }}
          servicesOptions={selectedProvider.services}
          requestFn={requestProviderQuote}
        />
      )}

      {selectedProvider && (
        <PlannerSuccessModal
          open={showQuoteSuccess}
          onClose={() => setShowQuoteSuccess(false)}
          title="Successful Message"
          subtitle="Thank you"
          businessName={selectedProvider?.businessName}
          isConsultant={false}
        />
      )}

      <CompareWidget
        compareList={compareList}
        onRemove={handleCompareToggle}
        onCompare={() => {
          if (compareList.length < 2) {
            setToast({
              open: true,
              message: "At least 2 items are needed for comparison",
              severity: "warning",
            });
          } else {
            setShowCompareScreen(true);
          }
        }}
        onClose={() => setCompareList([])}
      />

      <InfoPopup
        open={showCompareLimitPopup}
        onClose={() => setShowCompareLimitPopup(false)}
        note="You can select up to 5 providers for comparison."
        title="Comparison Limit"
      />
      <ToastMessage
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Container>
  );
}
